import { useCallback, useEffect, useState } from 'react'
import ApiService from '@services/api'
import {
  closeAdminPortalClientRequest,
  createAdminPortalClientRequest,
  downloadAdminPortalDocument,
  getAdminPortalClientRequests,
  uploadAdminPortalDocument,
} from '@services/portalService'
import { useNotification } from '@context/NotificationContext'
import './portalClientRequests.css'

const EMPTY_FORM = {
  clienteId: '',
  processoId: '',
  processoSearch: '',
  titulo: '',
  descricao: '',
  tipo: 'DOCUMENT',
  prazoResposta: '',
}

const STATUS = {
  OPEN: { label: 'Aguardando cliente', className: 'open' },
  ANSWERED: { label: 'Respondida', className: 'answered' },
  CLOSED: { label: 'Concluída', className: 'closed' },
}

function formatDate(value) {
  if (!value) return '—'
  const [year, month, day] = String(value).slice(0, 10).split('-')
  return `${day}/${month}/${year}`
}

function formatDateTime(value) {
  if (!value) return '—'
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))
}

function formatBytes(value) {
  const bytes = Number(value || 0)
  if (bytes < 1024 * 1024) return `${Math.max(1, bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function saveBlob(blob, name) {
  const url = window.URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = name || 'documento'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.URL.revokeObjectURL(url)
}

function RequestDocumentUploader({ request, onUploaded }) {
  const { showError, showSuccess } = useNotification()
  const [file, setFile] = useState(null)
  const [uploading, setUploading] = useState(false)

  const upload = async () => {
    if (!file) return
    setUploading(true)
    const result = await uploadAdminPortalDocument(file, {
      clienteId: request.clienteId,
      processoId: request.processoId,
      requestId: request.id,
      descricao: `Arquivo compartilhado para: ${request.titulo}`,
    })
    setUploading(false)
    if (!result.success) {
      showError(result.error || 'Não foi possível compartilhar o documento.')
      return
    }
    setFile(null)
    showSuccess('Documento compartilhado com o cliente.')
    onUploaded?.()
  }

  return (
    <div className="portal-office-upload">
      <label><input type="file" accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg,.txt,.csv" onChange={(event) => setFile(event.target.files?.[0] || null)} /><i className="fas fa-paperclip" /> {file ? file.name : 'Compartilhar documento'}</label>
      {file ? <button type="button" onClick={upload} disabled={uploading}>{uploading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-arrow-up" />} Enviar</button> : null}
    </div>
  )
}

export default function PortalClientRequestsTab() {
  const { showError, showSuccess } = useNotification()
  const [clients, setClients] = useState([])
  const [requests, setRequests] = useState([])
  const [status, setStatus] = useState('')
  const [clientFilter, setClientFilter] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [processOptions, setProcessOptions] = useState([])
  const [searchingProcesses, setSearchingProcesses] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const [requestsResult, clientsResult] = await Promise.all([
      getAdminPortalClientRequests({ clienteId: clientFilter || null, status }),
      ApiService.getProcessReportClients(),
    ])
    if (requestsResult.success) setRequests(Array.isArray(requestsResult.data) ? requestsResult.data : [])
    else { setRequests([]); showError(requestsResult.error || 'Não foi possível carregar as solicitações aos clientes.') }
    if (clientsResult.success) setClients(Array.isArray(clientsResult.data) ? clientsResult.data : [])
    setLoading(false)
  }, [clientFilter, status, showError])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (form.processoId || !form.clienteId || form.processoSearch.trim().length < 3) {
      setProcessOptions([])
      setSearchingProcesses(false)
      return undefined
    }
    const timer = window.setTimeout(async () => {
      setSearchingProcesses(true)
      const params = new URLSearchParams({
        clienteId: String(form.clienteId),
        busca: form.processoSearch.trim(),
        pagina: '1',
        tamanhoPagina: '10',
      })
      const result = await ApiService.request(`relatorios/processos/lista?${params}`)
      setSearchingProcesses(false)
      setProcessOptions(result.success ? (result.data?.itens || []) : [])
    }, 350)
    return () => window.clearTimeout(timer)
  }, [form.clienteId, form.processoId, form.processoSearch])

  const updateForm = (field, value) => setForm((current) => ({
    ...current,
    [field]: value,
    ...(field === 'clienteId' ? { processoId: '', processoSearch: '' } : {}),
  }))

  const createRequest = async (event) => {
    event.preventDefault()
    if (!form.clienteId) return showError('Selecione uma empresa.')
    setSaving(true)
    const result = await createAdminPortalClientRequest({
      clienteId: Number(form.clienteId),
      processoId: form.processoId ? Number(form.processoId) : null,
      titulo: form.titulo.trim(),
      descricao: form.descricao.trim(),
      tipo: form.tipo,
      prazoResposta: form.prazoResposta || null,
    })
    setSaving(false)
    if (!result.success) return showError(result.error || 'Não foi possível criar a solicitação.')
    showSuccess('Solicitação enviada ao Adjutorem IA.')
    setForm(EMPTY_FORM)
    setProcessOptions([])
    await load()
  }

  const closeRequest = async (request) => {
    if (!window.confirm(`Marcar “${request.titulo}” como concluída?`)) return
    const result = await closeAdminPortalClientRequest(request.id)
    if (!result.success) return showError(result.error || 'Não foi possível concluir a solicitação.')
    showSuccess('Solicitação concluída.')
    await load()
  }

  const download = async (document) => {
    const result = await downloadAdminPortalDocument(document.id)
    if (!result.success || !result.data) return showError(result.error || 'Não foi possível baixar o documento.')
    saveBlob(result.data, document.nome)
  }

  return (
    <div className="portal-client-admin">
      <section className="portal-client-admin-create">
        <div className="portal-client-admin-intro"><span>Adjutorem IA</span><h2>Solicitar informação ou documento</h2><p>Crie uma pendência visível para o cliente. Ela pode ser geral ou vinculada a um processo específico.</p></div>
        <form onSubmit={createRequest} className="portal-client-admin-form">
          <div className="portal-client-admin-grid">
            <label><span>Empresa *</span><select value={form.clienteId} onChange={(e) => updateForm('clienteId', e.target.value)} required><option value="">Selecione</option>{clients.map((client) => <option key={client.id} value={client.id}>{client.nome}</option>)}</select></label>
            <label><span>Tipo *</span><select value={form.tipo} onChange={(e) => updateForm('tipo', e.target.value)}><option value="DOCUMENT">Solicitar documento</option><option value="INFORMATION">Solicitar informação</option></select></label>
            <label className="wide portal-process-picker"><span>Processo <small>opcional</small></span><input value={form.processoSearch} onChange={(e) => { updateForm('processoSearch', e.target.value); updateForm('processoId', '') }} disabled={!form.clienteId} placeholder={form.clienteId ? 'Digite número, parte ou objeto...' : 'Selecione a empresa primeiro'} />{searchingProcesses ? <small>Buscando...</small> : null}{processOptions.length ? <div className="portal-process-options">{processOptions.map((process) => <button type="button" key={process.processoId} onClick={() => { setForm((current) => ({ ...current, processoId: process.processoId, processoSearch: process.numeroProcesso })); setProcessOptions([]) }}><strong>{process.numeroProcesso}</strong><span>{process.partePrincipal} × {process.parteContraria}</span></button>)}</div> : null}{form.processoId ? <small className="selected">Processo vinculado.</small> : null}</label>
            <label className="wide"><span>Título *</span><input value={form.titulo} onChange={(e) => updateForm('titulo', e.target.value)} required minLength={3} maxLength={180} placeholder="Ex.: Enviar contrato social atualizado" /></label>
            <label className="wide"><span>Orientação ao cliente *</span><textarea rows={3} value={form.descricao} onChange={(e) => updateForm('descricao', e.target.value)} required minLength={3} maxLength={3000} placeholder="Explique de forma clara o que precisamos e, se necessário, por quê." /></label>
            <label><span>Responder até <small>opcional</small></span><input type="date" value={form.prazoResposta} onChange={(e) => updateForm('prazoResposta', e.target.value)} /></label>
          </div>
          <button className="portal-client-create-button" type="submit" disabled={saving}>{saving ? <><i className="fas fa-spinner fa-spin" /> Criando...</> : <><i className="fas fa-paper-plane" /> Enviar ao cliente</>}</button>
        </form>
      </section>

      <section className="portal-client-admin-list">
        <header><div><h3>Acompanhamento</h3><p>Veja o que está aguardando o cliente e as respostas já recebidas.</p></div><div className="portal-client-admin-filters"><select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}><option value="">Todas as empresas</option>{clients.map((client) => <option key={client.id} value={client.id}>{client.nome}</option>)}</select><select value={status} onChange={(e) => setStatus(e.target.value)}><option value="">Todos os status</option><option value="OPEN">Aguardando cliente</option><option value="ANSWERED">Respondidas</option><option value="CLOSED">Concluídas</option></select></div></header>

        {loading ? <div className="portal-client-admin-empty"><i className="fas fa-spinner fa-spin" /> Carregando...</div> : requests.length === 0 ? <div className="portal-client-admin-empty"><i className="far fa-circle-check" /><span>Nenhuma solicitação neste filtro.</span></div> : (
          <div className="portal-client-admin-requests">{requests.map((request) => {
            const meta = STATUS[request.status] || { label: request.status, className: '' }
            return <article key={request.id} className="portal-client-admin-card">
              <div className="portal-client-admin-card-main">
                <div className="portal-client-admin-card-title"><div><span>{request.clienteNome}</span><h4>{request.titulo}</h4></div><span className={`state ${meta.className}`}>{meta.label}</span></div>
                <p>{request.descricao}</p>
                <div className="portal-client-admin-meta"><span><i className={`fas ${request.tipo === 'DOCUMENT' ? 'fa-file-arrow-up' : 'fa-circle-question'}`} /> {request.tipo === 'DOCUMENT' ? 'Documento' : 'Informação'}</span>{request.numeroProcesso ? <span><i className="fas fa-scale-balanced" /> {request.numeroProcesso}</span> : null}{request.prazoResposta ? <span><i className="far fa-calendar" /> Até {formatDate(request.prazoResposta)}</span> : null}<span><i className="far fa-clock" /> {formatDateTime(request.createdAt)}</span></div>
                {request.respostaCliente ? <div className="portal-client-answer"><span>Resposta do cliente</span><p>{request.respostaCliente}</p><small>{formatDateTime(request.respondidoEm)}</small></div> : null}
                {request.documentos?.length ? <div className="portal-client-admin-documents"><strong>Documentos</strong>{request.documentos.map((document) => <button type="button" key={document.id} onClick={() => download(document)}><i className="far fa-file-lines" /><span>{document.nome}<small>{document.origem === 'CLIENT' ? 'Cliente' : 'Veiga'} · {formatBytes(document.tamanhoBytes)}</small></span><i className="fas fa-arrow-down" /></button>)}</div> : null}
                {request.status !== 'CLOSED' ? <RequestDocumentUploader request={request} onUploaded={load} /> : null}
              </div>
              {request.status !== 'CLOSED' ? <aside><button type="button" onClick={() => closeRequest(request)}><i className="fas fa-check" /> Concluir</button></aside> : null}
            </article>
          })}</div>
        )}
      </section>
    </div>
  )
}
